import { yupResolver } from '@hookform/resolvers/yup';
import { Button, ButtonLevel } from '@vizit/atoms';
import { FormDialog, FormTextField } from '@vizit/forms';
import { Edit03Icon, LockIcon } from '@vizit/icons';
import { TextField } from '@vizit/molecules';
import { colors } from '@vizit/theme';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import * as yup from 'yup';
import { PLANNED_RETAILER_REQUIRED_MESSAGE } from '../create-new-product-dialog/planned-retailer-schema';
import { plannedRetailerSchema } from '../create-new-product-dialog/planned-retailer-schema';

const PLANNED_RETAILER_ERROR_MESSAGES = new Map<string, string>([
  ['PLANNED_RETAILER_REQUIRED', PLANNED_RETAILER_REQUIRED_MESSAGE],
  [
    'PLANNED_RETAILER_ITEM_ID_TAKEN',
    'Another page in this workspace already uses this planned retailer with the same Product ID.',
  ],
]);

const formSchema = yup.object({
  productTitle: yup.string().required('Product title is required'),
  brand: yup.string(),
  plannedRetailer: yup
    .string()
    .when('$showPlannedRetailer', ([showPlannedRetailer], s) => (showPlannedRetailer ? plannedRetailerSchema : s)),
});

type EditANewProductFormSchema = {
  productTitle: string;
  brand?: string;
  plannedRetailer?: string;
};

interface EditANewProductSubmissionError {
  /** The API's error code, when the response carried one */
  code?: string;
}

interface EditANewProductDialogProps {
  /**
   * Default values for the form
   */
  defaultValues?: EditANewProductFormSchema & { categoryName?: string };
  /**
   * The text for the trigger button
   *
   * @optional
   * @type {string}
   */
  triggerButtonText?: string;
  /**
   * Callback function to handle the edit new product api call
   */
  onSubmit: (data: EditANewProductFormSchema) => Promise<void>;
  /**
   * Whether the dialog is open (controlled mode)
   *
   * @optional
   * @type {boolean}
   */
  isOpen?: boolean;
  /**
   * Callback fired when the dialog open state changes (controlled mode)
   *
   * @optional
   * @type {(isOpen: boolean) => void}
   */
  onOpenChange?: (isOpen: boolean) => void;
  /**
   * Whether the planned retailer field shows, for a page staged for the Other retailer
   *
   * @optional
   * @default false
   */
  showPlannedRetailer?: boolean;
  /**
   * The last submission error. A new object per failed submission, so a repeated error shows again; a planned
   * retailer code puts its message on that field.
   *
   * @optional
   */
  submissionError?: EditANewProductSubmissionError | null;
}

function EditANewProductDialog(props: EditANewProductDialogProps) {
  const {
    defaultValues = {
      brand: '',
      productTitle: '',
      categoryName: '',
    },
    triggerButtonText = 'New product',
    onSubmit,
    isOpen,
    onOpenChange,
    showPlannedRetailer = false,
    submissionError,
  } = props;

  const methods = useForm<EditANewProductFormSchema>({
    resolver: yupResolver(formSchema) as unknown as Resolver<EditANewProductFormSchema>,
    context: { showPlannedRetailer },
    defaultValues: {
      productTitle: defaultValues.productTitle,
      brand: defaultValues.brand,
      plannedRetailer: defaultValues.plannedRetailer ?? '',
    },
    mode: 'all',
    delayError: 650,
  });

  const { control, handleSubmit, reset, setError } = methods;

  const handleOnResetForm = React.useCallback(() => {
    reset({
      brand: defaultValues.brand,
      productTitle: defaultValues.productTitle,
      plannedRetailer: defaultValues.plannedRetailer ?? '',
    });
  }, [reset, defaultValues.brand, defaultValues.productTitle, defaultValues.plannedRetailer]);

  React.useEffect(() => {
    const message = submissionError?.code ? PLANNED_RETAILER_ERROR_MESSAGES.get(submissionError.code) : undefined;
    if (message) {
      setError('plannedRetailer', { message });
    }
  }, [submissionError, setError]);

  return (
    <FormDialog
      formName="edit-product-info"
      formMethods={methods}
      isOpen={isOpen}
      formFieldsClassName="gap-8"
      onOpenChange={onOpenChange}
      onSubmit={handleSubmit(onSubmit)}
      onResetForm={handleOnResetForm}
    >
      <FormDialog.Trigger>
        <Button
          level={ButtonLevel.Secondary}
          aria-label={triggerButtonText}
          iconStart={<Edit03Icon size={20} color={colors['gray-utility']['400']} />}
        >
          {triggerButtonText}
        </Button>
      </FormDialog.Trigger>
      <FormDialog.Title>Edit product info</FormDialog.Title>
      <FormDialog.Subtitle>
        {showPlannedRetailer
          ? "Edit your product's title, brand or planned retailer to keep it up to date."
          : "Edit your product's title or brand to keep it up to date."}
      </FormDialog.Subtitle>
      <FormDialog.FormFields>
        {/* Technically this is a text field, but it's not a form field so we need to use the TextField component */}
        <TextField
          label="Category"
          value={defaultValues.categoryName || ''}
          isRequired
          isDisabled
          description="Category cannot be changed"
          startIcon={<LockIcon size={20} />}
        />
        {showPlannedRetailer && (
          <FormTextField
            control={control}
            className="min-h-0"
            name="plannedRetailer"
            label="Planned retailer"
            placeholder="e.g. Costco US, Target US, Ulta"
            isRequired
          />
        )}
        <FormTextField control={control} className="min-h-0" name="productTitle" label="Product title" isRequired />
        <FormTextField control={control} className="min-h-0" name="brand" label="Brand" />
      </FormDialog.FormFields>
      <FormDialog.SubmitButton>Save info</FormDialog.SubmitButton>
    </FormDialog>
  );
}

export { EditANewProductDialog };
export type { EditANewProductFormSchema, EditANewProductDialogProps, EditANewProductSubmissionError };
